/**
 * 悟空IM WebSocket连接管理（WuKongIMSDK版本）
 *
 * @description 使用 WuKongIMSDK 管理WebSocket连接，处理实时消息、事件推送
 *              现在使用全局 store 管理连接状态，支持全局消息监听
 * @author system
 * @date 2025-11-04
 * @see https://docs.githubim.com/zh/sdk/wukongim/javascript/integration
 */

import WKSDK from 'wukongimjssdk'
import {
  useWuKongIMStore,
  messageCallbackManager,
} from '~/stores/wukongIM'

export const useWuKongIM = () => {
  const config = useRuntimeConfig()
  const wukongUrl = (config.public.WUKONG_WS_URL as string)

  // 使用全局 store 管理状态
  const wukongIMStore = useWuKongIMStore()

  // 计算属性 - 从 store 获取状态
  const isConnected = computed(() => wukongIMStore.isConnected)
  const isConnecting = computed(() => wukongIMStore.isConnecting)

  /**
   * 连接到悟空IM
   * 如果全局已连接，则直接返回，不再重复连接
   */
  const connect = async (userId: string, token: string) => {
    console.log('🚀 [useWuKongIM] connect 被调用, userId:', userId)
    
    // 已连接或正在连接则跳过
    if (wukongIMStore.isConnected || wukongIMStore.isConnecting) {
      console.log('🚀 [useWuKongIM] 已连接或正在连接，跳过', { isConnected: wukongIMStore.isConnected, isConnecting: wukongIMStore.isConnecting })
      return
    }

    try {
      // 连接悟空之前调用customer-service/session/refresh接口刷新token
      await useHttp.post('/api/customer-service/session/refresh', { deviceFlag: 2, deviceLevel: 0 })
      
      wukongIMStore.setConnecting(true)
      // 初始化 SDK（单例模式）
      const sdk = WKSDK.shared()

      // 配置连接参数
      sdk.config.addr = wukongUrl
      sdk.config.uid = userId
      sdk.config.token = token
      sdk.config.deviceFlag = 2 // WEB端
      // @ts-ignore - deviceLevel 在某些版本的 SDK 中存在
      sdk.config.deviceLevel = 0 // 主设备

      console.log('✅ 悟空IM配置完成:', {
        addr: wukongUrl,
        uid: userId,
        deviceFlag: sdk.config.deviceFlag,
        tokenPrefix: token.substring(0, 20) + '...',
      })

      // 监听连接状态
      sdk.connectManager.addConnectStatusListener(
        (status: number, reasonCode?: number) => {
          console.log('悟空IM连接状态变化:', { status, reasonCode })

          if (status === 1) {
            // 已连接
            wukongIMStore.setConnected(true)
            wukongIMStore.setConnecting(false)
            wukongIMStore.setSdk(sdk)
            console.log('✅ 悟空IM连接成功')
            messageCallbackManager.notifyConnect()
          } else if (status === 0) {
            // 断开连接
            wukongIMStore.setConnected(false)
            wukongIMStore.setConnecting(false)
            console.log('❌ 悟空IM已断开')
            messageCallbackManager.notifyDisconnect()
            import('fundebug-javascript').then(fundebug => {
            fundebug.notify('❌ 悟空IM已断开', '❌ 悟空IM已断开', {
                metaData: { pageUrl: window.location.href }
              })
            })
          }
        }
      )
      
      const route = useRoute()
      // 监听消息
      const messageListener = (message: any) => {
        console.log('📨 收到悟空IM消息:', message)
        if (message.fromUID !== userId) {
          wukongIMStore.incrementUnreadCount(1)
          wukongIMStore.updateLastMessageTime()
           ElNotification({
            title: '💬 新消息',
            message: `<div style="cursor: pointer; display: flex; align-items: center; gap: 10px; padding: 4px 0;">
              <div style="width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0;">
                ${(message.content.contentObj.sender.nickName || '客').slice(0, 1)}
              </div>
              <div style="flex: 1; min-width: 0;">
                <div style="font-size: 13px; color: #909399;">给您发来了一条新消息，点击查看</div>
              </div>
              <div style="color: #1890ff; font-size: 20px;">›</div>
            </div>`,
            position: 'bottom-right',
            duration: 4000,
            dangerouslyUseHTMLString: true,
            onClick: () => {
              const { openService: openCustomerService } = useCustomerService()
              openCustomerService({ uid: message.content.contentObj.sender.id })
            },
          })
        }
        // 通过全局回调管理器通知所有监听者
        messageCallbackManager.notifyMessage(message)
      }

      sdk.chatManager.addMessageListener(messageListener)

      // 连接
      await sdk.connect()

    } catch (error) {
      console.error('连接悟空IM失败:', error)
      import('fundebug-javascript').then(fundebug => {
        fundebug.notify('连接悟空IM失败', '连接悟空IM失败catch,106行', {
          metaData: { pageUrl: window.location.href }
        })
      })
      wukongIMStore.setConnecting(false)
      throw error
    }
  }

  /**
   * 断开连接
   * 注意：全局连接模式下，一般不需要在页面卸载时断开
   */
  const disconnect = () => {
    try {
      const sdk = wukongIMStore.sdk
      if (sdk && sdk.disconnect) {
        sdk.disconnect()
        console.log('悟空IM已断开连接')
      } else {
        console.log('悟空IM未初始化，无需断开')
      }

      // 重置状态
      wukongIMStore.setConnected(false)
      wukongIMStore.setConnecting(false)
      wukongIMStore.setSdk(null)
    } catch (error) {
      console.error('断开悟空IM连接失败:', error)
    }
  }

  /**
   * 发送文本消息
   */
  const sendMessage = async (
    channelId: string,
    channelType: number,
    content: string
  ) => {
    const sdk = wukongIMStore.sdk
    if (!wukongIMStore.isConnected || !sdk) {
      throw new Error('未连接到悟空IM')
    }

    try {
      // 创建文本消息
      const message = sdk.chatManager.createTextMessage(content)

      // 设置频道
      message.channel = { channelID: channelId, channelType: channelType }

      // 发送消息
      await sdk.chatManager.send(message)
      return message
    } catch (error) {
      console.error('发送消息失败:', error)
      throw error
    }
  }

  /**
   * 注册消息接收回调
   * 使用全局回调管理器，支持多个页面同时监听
   */
  const onMessage = (callback: (message: any) => void) => {
    return messageCallbackManager.addMessageCallback(callback)
  }

  /**
   * 注册事件接收回调（兼容旧代码）
   */
  const onEvent = (callback: (event: any) => void) => {
    // WuKongIMSDK 中事件是通过 CMDManager 处理的
    // 暂时保留此方法以保持兼容性
    return () => {}
  }

  /**
   * 注册连接成功回调
   */
  const onConnect = (callback: () => void) => {
    return messageCallbackManager.addConnectCallback(callback)
  }

  /**
   * 注册断开连接回调
   */
  const onDisconnect = (callback: () => void) => {
    return messageCallbackManager.addDisconnectCallback(callback)
  }

  /**
   * 获取 SDK 实例
   */
  const getSdk = () => wukongIMStore.sdk

  // 注意：移除了 onUnmounted 中的 disconnect 调用
  // 因为现在是全局连接，不需要在页面卸载时断开

  return {
    isConnected,
    isConnecting,
    connect,
    disconnect,
    sendMessage,
    onMessage,
    onEvent,
    onConnect,
    onDisconnect,
    sdk: computed(() => wukongIMStore.sdk), // 暴露 SDK 实例以便高级使用
    getSdk,
  }
}
